import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  LinkIcon, 
  VideoIcon, 
  CheckIcon,
  UserIcon,
  StarIcon,
  EditIcon,
  ArrowLeftIcon,
  ClockIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  PencilIcon
} from 'lucide-react';
import { 
  createInterview, 
  getInterviews,
  generateInterviewLink,
  updateInterviewQuestions
} from '../services/supabase';
import { useInterviewStore } from '../store';
import { InterviewQuestionEditor } from './InterviewQuestionEditor';
import toast from 'react-hot-toast';

export function InterviewManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [editingInterview, setEditingInterview] = useState<string | null>(null);
  const jobDescription = useInterviewStore((state) => state.jobDescription);
  const interviewConfig = useInterviewStore((state) => state.interviewConfig);

  useEffect(() => {
    loadInterviews();
  }, []);

  const loadInterviews = async () => {
    try {
      const data = await getInterviews();
      setInterviews(data || []);
    } catch (error) {
      console.error('Error loading interviews:', error);
      toast.error(t('errors.loadingInterviews'));
    } finally {
      setLoading(false);
    }
  };

  const createNewInterview = async () => {
    if (!jobDescription) {
      toast.error(t('errors.noJobDescription'));
      return;
    }

    try {
      setLoading(true);
      const interview = await createInterview(jobDescription.title, {
        ...interviewConfig,
        jobDescription
      });
      
      setInterviews(prev => [interview, ...prev]);
      setEditingInterview(interview.id);
      toast.success(t('interview.management.created'));
    } catch (error) {
      console.error('Error creating interview:', error);
      toast.error(t('errors.creatingInterview'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionsSaved = () => {
    setEditingInterview(null);
    loadInterviews();
  };

  const toggleInterviewStatus = async (interview: any) => {
    try {
      const newStatus = interview.status === 'active' ? 'draft' : 'active';
      await updateInterviewQuestions(interview.id, interview.config.questions || [], newStatus);
      loadInterviews();
      toast.success(`Interview ${newStatus === 'active' ? 'activated' : 'set to draft'}`);
    } catch (error) {
      console.error('Error updating interview status:', error);
      toast.error('Failed to update interview status');
    }
  };

  const copyShareLink = async (token: string) => {
    const shareUrl = generateInterviewLink(token);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(token);
      toast.success(t('interview.management.linkCopied'));
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error(t('interview.management.copyError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('interview.management.title')}
          </h2>
          {editingInterview && (
            <button
              onClick={() => setEditingInterview(null)}
              className="mt-2 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Interview List
            </button>
          )}
        </div>
        <button
          onClick={createNewInterview}
          disabled={!jobDescription}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-5 w-5" />
          {t('interview.management.create')}
        </button>
      </div>

      {/* Question Editor */}
      {editingInterview && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <InterviewQuestionEditor
            interviewId={editingInterview}
            onSave={handleQuestionsSaved}
          />
        </div>
      )}

      {/* Interview List */}
      {!editingInterview && interviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {interviews.map((interview) => (
            <div
              key={interview.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
            >
              {/* Interview Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {interview.job_title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {new Date(interview.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(interview.status)}`}>
                    {t(`interview.status.${interview.status}`)}
                  </span>
                  {interview.interview_responses?.length > 0 && (
                    <div className="flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded-full">
                      <UserIcon className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm font-medium text-indigo-600">
                        {interview.interview_responses.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                {interview.status !== 'completed' && (
                  <button
                    onClick={() => setEditingInterview(interview.id)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    <EditIcon className="h-5 w-5" />
                    {t('interview.management.editQuestions')}
                  </button>
                )}

                {interview.status !== 'completed' && (
                  <button
                    onClick={() => toggleInterviewStatus(interview)}
                    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md ${
                      interview.status === 'active'
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {interview.status === 'active' ? (
                      <>
                        <PencilIcon className="h-5 w-5" />
                        Set to Draft
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5" />
                        Activate Interview
                      </>
                    )}
                  </button>
                )}

                {interview.status === 'active' && (
                  <button
                    onClick={() => copyShareLink(interview.share_token)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200"
                  >
                    {copiedLink === interview.share_token ? (
                      <CheckIcon className="h-5 w-5" />
                    ) : (
                      <LinkIcon className="h-5 w-5" />
                    )}
                    {t('interview.management.copyLink')}
                  </button>
                )}

                {interview.interview_responses?.length > 0 && (
                  <button
                    onClick={() => navigate(`/results/${interview.id}`)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    <VideoIcon className="h-5 w-5" />
                    {t('interview.management.viewResults')}
                  </button>
                )}
              </div>

              {/* Recent Candidates */}
              {interview.interview_responses?.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t('interview.management.recentCandidates')}
                  </h4>
                  <div className="space-y-2">
                    {interview.interview_responses.slice(0, 3).map((response: any) => (
                      <div key={response.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <UserIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{response.candidate_name}</div>
                            <div className="text-sm text-gray-500">{response.candidate_email}</div>
                          </div>
                        </div>
                        {response.score !== null && (
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(response.score)}`}>
                            {response.score}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!editingInterview && interviews.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">
            {t('interview.management.noInterviews')}
          </p>
        </div>
      )}
    </div>
  );
}